import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import { Edition, useEditionValue } from "shared/lib/edition";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { HomeV2SectionStoryBlock } from "scmp-app/components/home-v2/section-story-block";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { FallbackReason, WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { secondaryStoryPackageHomeV2Query$key } from "scmp-app/queries/__generated__/secondaryStoryPackageHomeV2Query.graphql";

import { useHomeV2StoryPackageTopic } from "./hooks";
import { Container, Dash, Description, Header, Title, TitleLink } from "./styles";

const placeholderDescription =
  "Global experts discuss US-China ties, the Chinese economy and more.";

type Props = {
  className?: string;
  fallbackReason?: FallbackReason;
  reference: secondaryStoryPackageHomeV2Query$key;
  widgetVersion?: WidgetVersion;
};

export const HomeV2SecondaryStoryPackage: FunctionComponent<Props> = ({
  className,
  fallbackReason,
  reference,
  widgetVersion,
}) => {
  const data = useFragment(
    graphql`
      fragment secondaryStoryPackageHomeV2Query on Query
      @argumentDefinitions(secondaryStoryPackageArticlesQueueName: { type: "String!" }) {
        secondaryStoryPackageV2Articles: queue(
          filter: { name: $secondaryStoryPackageArticlesQueueName }
        ) {
          ...hooksHomeV2StoryPackageTopicQueue
          items(first: 4) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...sectionStoryBlockHomeV2QueueItemsEdge
            }
          }
        }
      }
    `,
    reference,
  );

  const queue = data.secondaryStoryPackageV2Articles;
  const { name, term } = useHomeV2StoryPackageTopic(queue);
  const items = queue?.items?.edges ?? [];
  const listOfArticles = items.map(item => item.node.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: name,
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        result: fallbackReason ? "error" : "valid",
        ...(fallbackReason && { result_error: fallbackReason }),
        widget_name: "feature_package_2",
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    }),
    [currentPageType, fallbackReason, listOfArticles, name, widgetVersion],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const currentEdition = useEditionValue();

  if (items.length === 0) return null;

  const titleNode =
    term && (term.__typename === "Section" || term.__typename === "Topic") ? (
      <TitleLink reference={term}>{name}</TitleLink>
    ) : (
      <Title>{name}</Title>
    );

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.StoryPackageTwo }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <HomeV2SectionStoryBlock
          header={
            <Header>
              {titleNode}
              {/* SCMPWEB-9609: placeholder description is not relevant for Asia edition, show only for HK/International */}
              {[Edition.HongKong, Edition.International].includes(currentEdition) && (
                <>
                  <Dash>-</Dash>
                  <Description>{placeholderDescription}</Description>
                </>
              )}
            </Header>
          }
          onClick={(entityId, urlAlias) => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                block_section: name,
                destination_url: getAbsoluteUrl(urlAlias),
                list_of_articles: listOfArticles,
                page_type: currentPageType,
                recommendation_id: "",
                result: fallbackReason ? "error" : "valid",
                ...(fallbackReason && { result_error: fallbackReason }),
                widget_name: "feature_package_2",
                ...(widgetVersion && { widget_version: widgetVersion }),
              },
              subcategory: "widget",
            });
          }}
          reference={items}
          withSection={false}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2SecondaryStoryPackage.displayName = "HomeV2SecondaryStoryPackage";
